import { AnalyticsEvent, trackEvent } from '@/lib/analytics';
import { MS_PER_DAY } from '@/lib/date';
import type { BillingBannerKind, BillingSummary, UsageBannerKind } from '@/lib/billing';

/**
 * `trial_banner_interacted` — one PostHog event shared by every upgrade /
 * extend CTA (sidebar `PlanCard`, `BillingAlertBanner`, `UsageAlertBanner`,
 * `UsageLimitDialog`). Keys and `action` values are webui's
 * (`home/helpers/trial-analytics.ts`); its funnel insights filter on them.
 */
export type TrialBannerAction = 'upgrade_clicked' | 'extend_clicked';

/** webui's fallback when the trial window is missing or unparseable. */
const DEFAULT_TOTAL_TRIAL_DAYS = 7;

/**
 * webui's base props, minus `warmup_enabled_accounts`: the billing summary these
 * surfaces read carries no warmup-enabled count, and fetching one only to
 * decorate a click event isn't worth a request.
 */
export type TrialBannerBaseProperties = {
  total_trial_days: number;
  email_accounts_connected: number;
};

export function buildTrialBannerBaseProperties(
  summary: BillingSummary | undefined,
): TrialBannerBaseProperties {
  const start = Date.parse(summary?.trial?.startedAt ?? '');
  const end = Date.parse(summary?.trial?.endsAt ?? '');
  const days = Math.ceil((end - start) / MS_PER_DAY);
  return {
    total_trial_days: Number.isFinite(days) && days > 0 ? days : DEFAULT_TOTAL_TRIAL_DAYS,
    email_accounts_connected: summary?.emailAccounts.used ?? 0,
  };
}

export function trackTrialBannerInteracted(
  summary: BillingSummary | undefined,
  action: TrialBannerAction,
  /** Which banner fired it; the sidebar card sends none. */
  kind?: BillingBannerKind | UsageBannerKind,
): void {
  trackEvent(AnalyticsEvent.TrialBannerInteracted, {
    action,
    ...(kind ? { kind } : {}),
    ...buildTrialBannerBaseProperties(summary),
  });
}
